// Slice messaggi: fetch completo, letto/non letto, archivia/cestina, allegati.
// Logica identica a gmailStore.ts.
import type { MailAdapter } from '../mailAdapter';
import type { MailActions, MailSet, MailGet } from '../storeTypes';
import { unreadCountForView, propagateReadState } from '../helpers';

type MessagesSlice = Pick<
  MailActions,
  'loadFullMessage' | 'markAsRead' | 'markAsUnread' | 'archiveThread' | 'trashThread' | 'moveThreadToFolder' | 'downloadAttachment'
>;

export function createMessagesSlice(set: MailSet, get: MailGet, adapter: MailAdapter): MessagesSlice {
  const LOG = `[mailStore:${adapter.provider}]`;
  const allMessages = () => [...get().threads.flatMap(t => t.messages), ...get().selectedMessages];

  return {
    loadFullMessage: async (organizationId, userId, messageId) => {
      try {
        return await adapter.fetchFullMessage(organizationId, userId, messageId);
      } catch (error: any) {
        console.error(`${LOG} Load full message error:`, error);
      }
    },

    markAsRead: async (organizationId, userId, messageIds) => {
      try {
        await adapter.updateReadFlag(organizationId, userId, messageIds, true);

        // Propaga al provider (rimuove label UNREAD). Si attende in fondo, dopo lo stato
        // locale: cosi' "Segna come non letto" non viene superato da questa lettura.
        const providerDone = propagateReadState(allMessages, adapter, organizationId, userId, messageIds, 'markRead');

        // Update local state
        const threads = get().threads.map(t => ({
          ...t,
          messages: t.messages.map(m =>
            messageIds.includes(m.id) ? { ...m, is_read: true } : m
          ),
          has_unread: t.messages.some(m => !messageIds.includes(m.id) && !m.is_read),
        }));
        const unreadCount = unreadCountForView(get().activeLabel, threads, get().unreadCount);
        set({ threads, unreadCount });
        await providerDone;
      } catch (error: any) {
        console.error(`${LOG} Mark as read error:`, error);
      }
    },

    markAsUnread: async (organizationId, userId, messageIds) => {
      try {
        await adapter.updateReadFlag(organizationId, userId, messageIds, false);

        // Propaga al provider (aggiunge label UNREAD) — fire-and-forget
        propagateReadState(allMessages, adapter, organizationId, userId, messageIds, 'markUnread');

        const threads = get().threads.map(t => ({
          ...t,
          messages: t.messages.map(m =>
            messageIds.includes(m.id) ? { ...m, is_read: false } : m
          ),
          has_unread: t.messages.some(m => messageIds.includes(m.id) || !m.is_read),
        }));
        const unreadCount = unreadCountForView(get().activeLabel, threads, get().unreadCount);
        set({ threads, unreadCount });
      } catch (error: any) {
        console.error(`${LOG} Mark as unread error:`, error);
      }
    },

    archiveThread: async (organizationId, userId, threadId) => {
      try {
        const thread = get().threads.find(t => t.thread_id === threadId);
        if (!thread) return false;

        // Archive all messages in thread (parallel)
        await Promise.all(thread.messages.map(msg =>
          adapter.modify(organizationId, userId, msg.message_id, 'archive')
        ));

        // Remove thread from local state
        const threads = get().threads.filter(t => t.thread_id !== threadId);
        const unreadCount = unreadCountForView(get().activeLabel, threads, get().unreadCount);
        set({ threads, unreadCount, selectedThreadId: null, selectedMessages: [] });
        return true;
      } catch (error: any) {
        console.error(`${LOG} Archive error:`, error);
        return false;
      }
    },

    trashThread: async (organizationId, userId, threadId) => {
      try {
        const thread = get().threads.find(t => t.thread_id === threadId);
        if (!thread) return false;

        await Promise.all(thread.messages.map(msg =>
          adapter.modify(organizationId, userId, msg.message_id, 'trash')
        ));

        const threads = get().threads.filter(t => t.thread_id !== threadId);
        const unreadCount = unreadCountForView(get().activeLabel, threads, get().unreadCount);
        set({ threads, unreadCount, selectedThreadId: null, selectedMessages: [] });
        return true;
      } catch (error: any) {
        console.error(`${LOG} Trash error:`, error);
        return false;
      }
    },

    moveThreadToFolder: async (organizationId, userId, threadId, folderId) => {
      try {
        const thread = get().threads.find(t => t.thread_id === threadId);
        if (!thread) return false;

        // Sposta ogni messaggio del thread nella cartella destinazione (parallelo).
        await Promise.all(thread.messages.map(msg =>
          adapter.modify(organizationId, userId, msg.message_id, 'move', folderId)
        ));

        // Rimozione dalla vista corrente SOLO se non più pertinente. Lo spostamento di
        // cartella non tocca flag né categorie: nelle viste NON folder-scoped (ALL,
        // STARRED, label-categoria) il thread resta pertinente. Si rimuove solo dalle
        // viste cartella (INBOX/SENT/DRAFT/FOLDER:*) diverse dalla destinazione.
        const activeLabel = get().activeLabel;
        const folderScoped = activeLabel === 'INBOX' || activeLabel === 'SENT'
          || activeLabel === 'DRAFT' || activeLabel.startsWith('FOLDER:');
        const stillPertinent = !folderScoped || activeLabel === `FOLDER:${folderId}`;
        if (!stillPertinent) {
          const threads = get().threads.filter(t => t.thread_id !== threadId);
          const unreadCount = unreadCountForView(get().activeLabel, threads, get().unreadCount);
          set({ threads, unreadCount, selectedThreadId: null, selectedMessages: [] });
        }
        return true;
      } catch (error: any) {
        console.error(`${LOG} Move to folder error:`, error);
        return false;
      }
    },

    downloadAttachment: async (organizationId, userId, messageId, attachmentId, filename, mimeType) => {
      try {
        const base64 = await adapter.downloadAttachment(organizationId, userId, messageId, attachmentId);

        // Convert base64 to blob and trigger download
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error: any) {
        console.error(`${LOG} Download attachment error:`, error);
        throw error;
      }
    },
  };
}
